import React, { useEffect, useState } from "react";
import { Box, TextField } from "@mui/material";
import Header from "./Header";
import CollapsiblePanel from "./CollapsiblePanel";
import History from "./History";
import Parameters from "./Parameters";
import Splines from "./Splines";
import DataInput from "./DataInput";
import { getInitialState } from "./DefaultParameters";

/**
 * Response tab layout. Calls POST /api/v1/microslide/response.
 *
 * Mirrors MicroTip/CalibrationLayout.js: header at top, then collapsible
 * Results / Read Data / Splines / Parameters panels.
 */
export default function ResponseLayout() {
    const [resultsCollapsed, setResultsCollapsed] = useState(true);
    const [dataCollapsed, setDataCollapsed] = useState(false);
    const [splinesCollapsed, setSplinesCollapsed] = useState(true);
    const [paramsCollapsed, setParamsCollapsed] = useState(true);

    const [rows, setRows] = useState([{ signal: "", reference: "", dark: "", time: "" }]);
    const [parameters, setParameters] = useState(getInitialState());
    const [splines, setSplines] = useState({
        preprocessing: null, g1: null, g2: null, g3: null, g4: null
    });
    const [extras, setExtras] = useState({ iLevel: "0", iRep: "0", wcf: "0.9894", bcf: "0" });

    const [resultDataMap, setResultDataMap] = useState(() => {
        const saved = sessionStorage.getItem("microslideHistory_response");
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        if (resultDataMap && Object.keys(resultDataMap).length > 0) {
            setResultsCollapsed(false);
        }
    }, [resultDataMap]);

    return (
        <div>
            <Header
                mode="response"
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                extras={{ ...extras, splines }}
                setResultDataMap={setResultDataMap}
                onRun={() => setResultsCollapsed(false)}
            />

            <Box display="flex" flexDirection="column" padding={2}>
                <CollapsiblePanel
                    title="Results"
                    collapsed={resultsCollapsed}
                    onToggle={() => setResultsCollapsed(!resultsCollapsed)}
                >
                    <History
                        resultDataMap={resultDataMap}
                        resultFields={["Response1", "Response2", "FirstDr", "SubDep", "ConditionCode", "Error"]}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Read Data"
                    collapsed={dataCollapsed}
                    onToggle={() => setDataCollapsed(!dataCollapsed)}
                >
                    <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={1} mb={2}>
                        <TextField size="small" label="iLevel" type="number"
                            value={extras.iLevel} onChange={e => setExtras({ ...extras, iLevel: e.target.value })} />
                        <TextField size="small" label="iRep" type="number"
                            value={extras.iRep} onChange={e => setExtras({ ...extras, iRep: e.target.value })} />
                        <TextField size="small" label="WCF"
                            value={extras.wcf} onChange={e => setExtras({ ...extras, wcf: e.target.value })} />
                        <TextField size="small" label="BCF"
                            value={extras.bcf} onChange={e => setExtras({ ...extras, bcf: e.target.value })} />
                    </Box>
                    <DataInput
                        columns={[
                            { key: "signal", label: "Signal" },
                            { key: "reference", label: "Reference" },
                            { key: "dark", label: "Dark" },
                            { key: "time", label: "Time (s)" }
                        ]}
                        rows={rows}
                        setRows={setRows}
                        placeholder={"Signal\tReference\tDark\tTime\n100\t50\t10\t0\n..."}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Splines"
                    collapsed={splinesCollapsed}
                    onToggle={() => setSplinesCollapsed(!splinesCollapsed)}
                >
                    <Splines splines={splines} setSplines={setSplines} />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Assay Parameters"
                    collapsed={paramsCollapsed}
                    onToggle={() => setParamsCollapsed(!paramsCollapsed)}
                >
                    <Parameters parameters={parameters} setParameters={setParameters} />
                </CollapsiblePanel>
            </Box>
        </div>
    );
}
